import { usePagination } from '@/shared/ui/navigation/Pagination/hooks/usePagination';
import { OrderSearchView } from '@/entities/order';
import { useEffect, useRef } from 'react';
import { useSearchParams } from 'next/navigation';

export interface UseOrderPaginationOptions {
  data: OrderSearchView | null;
  onPageChange?: (cursor: string | null) => void;
  initialPageSize?: number;
  /** 탭 컨텍스트의 params (탭별 독립적인 상태를 위해 사용) */
  tabParams?: Record<string, unknown>;
  /** 탭 params 업데이트 함수 */
  onTabParamsUpdate?: (params: Record<string, unknown>) => void;
}

export interface UseOrderPaginationReturn {
  cursor: string | null;
  currentPageIndex: number;
  pageSize: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  goToPrevious: () => void;
  goToNext: () => void;
  setPageSize: (pageSize: number) => void;
  setCursorAndIndex: (cursor: string | null, index: number) => void;
}

/**
 * 주문 페이지네이션 훅
 * OrderSearchView와 usePagination을 통합
 */
export function useOrderPagination({
  data,
  onPageChange,
  initialPageSize = 20,
  tabParams,
  onTabParamsUpdate,
}: UseOrderPaginationOptions): UseOrderPaginationReturn {
  const hasNext = data?.hasNext ?? false;
  const nextCursor = data?.nextCursor ?? null;
  const searchParams = useSearchParams();
  const useTabParams = !!tabParams && !!onTabParamsUpdate;
  const pageCursorFromUrl = !useTabParams ? (searchParams?.get('page') ?? null) : null;
  const pageCursorFromTab = useTabParams
    ? ((tabParams['page'] as string | null | undefined) ?? null)
    : null;
  const currentPageCursor = useTabParams ? pageCursorFromTab : pageCursorFromUrl;
  const onPageChangeRef = useRef(onPageChange);
  const lastCursorRef = useRef<string | null>(null);

  // onPageChange ref 업데이트
  useEffect(() => {
    onPageChangeRef.current = onPageChange;
  }, [onPageChange]);

  // 현재 커서는 탭 params 또는 URL에서 가져오거나 null (첫 페이지)
  // usePagination이 탭 params 또는 URL에서 관리하므로 여기서는 null로 설정
  const currentCursor = null;

  const pagination = usePagination({
    initialCursor: currentCursor,
    pageSize: initialPageSize,
    // hasPreviousPage는 usePagination 내부에서 pageIndex > 1로 판단하므로
    // 여기서는 기본값으로 false를 전달 (실제로는 pageIndex로 판단됨)
    hasPreviousPage: false,
    hasNextPage: hasNext,
    nextCursor,
    syncWithUrl: !useTabParams,
    queryKey: 'page',
    tabParams,
    onTabParamsUpdate,
    onPageChange: (event) => {
      onPageChangeRef.current?.(event.cursor);
    },
  });

  // 탭 params 또는 URL의 cursor 변경 감지하여 onPageChange 호출
  useEffect(() => {
    // cursor가 변경되었고, 이전 cursor와 다를 때만 호출
    if (currentPageCursor !== lastCursorRef.current) {
      lastCursorRef.current = currentPageCursor;
      // cursor가 실제로 변경되었을 때만 onPageChange 호출
      if (currentPageCursor !== pagination.cursor) {
        onPageChangeRef.current?.(currentPageCursor);
      }
    }
  }, [currentPageCursor, pagination.cursor]);

  return {
    cursor: pagination.cursor,
    currentPageIndex: pagination.currentPageIndex,
    pageSize: pagination.pageSize,
    hasPreviousPage: pagination.hasPreviousPage,
    hasNextPage: pagination.hasNextPage,
    goToPrevious: pagination.goToPrevious,
    goToNext: pagination.goToNext,
    setPageSize: pagination.setPageSize,
    setCursorAndIndex: pagination.setCursorAndIndex,
  };
}
